/**
 * Login Page Component - Exact Figma Design + Cognito Authentication
 * NO BOX, NO SHADOWS - Following Figma pattern exactly
 */

import CompanySsoSignIn from './CompanySsoSignIn.jsx';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { ArrowLeft, CreditCard, Shield } from 'lucide-react';
import {
  createStripeSetupSession,
  isStripeCheckoutEnabled,
  STRIPE_SETUP_PENDING_KEY,
} from '@/services/stripeBilling';
import { getPendingInviteToken } from '@/services/workspaceApi';
import { initializeCognitoConfig } from '@/services/cognitoService';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import { APP_BROWSE_DEFAULT_HOME } from '@stepflow/ui-shared/constants/appBrowseFlags.js';
import { homeFor } from '@stepflow/ui-shared/utils/lastPlace.js';

// Post-login landing for network-workbench roles: Apps browse home when the flag
// is on, else the classic create flow. USER role always lands on /agentic.
const workbenchLanding = () => (APP_BROWSE_DEFAULT_HOME ? '/registry' : '/network/new');
import {
  isGoogleOAuthConfigured,
  startGoogleHostedUISignIn,
} from '@/services/cognitoOAuth';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signUp, confirmSignUp, resendSignUp, forgotPassword, confirmPassword } = useAuth();
  
  const [activeView, setActiveView] = useState('login'); // 'login', 'signup', 'reset'
  const [direction, setDirection] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Login state (Cognito username is email)
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Signup state
  const [signupData, setSignupData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    name: '',
  });
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [showPostVerifyBilling, setShowPostVerifyBilling] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  
  // Forgot password state
  const [forgotUsername, setForgotUsername] = useState('');
  const [resetData, setResetData] = useState({
    code: '',
    newPassword: ''
  });
  const [awaitingReset, setAwaitingReset] = useState(false);

  const switchView = (newView) => {
    if (isTransitioning) return;
    
    const viewOrder = ['login', 'signup', 'reset'];
    const currentIndex = viewOrder.indexOf(activeView);
    const newIndex = viewOrder.indexOf(newView);
    setDirection(newIndex > currentIndex ? 1 : -1);
    
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveView(newView);
      setError('');
      setSuccess('');
      setAwaitingConfirmation(false);
      setAwaitingReset(false);
      setShowPostVerifyBilling(false);
      setIsTransitioning(false);
    }, 300);
  };

  // Handle Login
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await initializeCognitoConfig();
      await startGoogleHostedUISignIn();
    } catch (err) {
      setError(err.message || 'Could not start Google sign-in');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn(loginData.email.trim(), loginData.password);
      if (result.success) {
        const pendingStripe = localStorage.getItem(STRIPE_SETUP_PENDING_KEY) === '1';
        if (pendingStripe && isStripeCheckoutEnabled()) {
          localStorage.removeItem(STRIPE_SETUP_PENDING_KEY);
          try {
            const checkoutUrl = await createStripeSetupSession({
              successPath: canAccessNetworkWorkbench(result.user?.stepflowRole)
                ? workbenchLanding()
                : '/agentic',
            });
            window.location.assign(checkoutUrl);
            return;
          } catch (stripeErr) {
            setError(
              stripeErr.message ||
                'Could not open Stripe Checkout. Complete plan selection on the pricing page.'
            );
            navigate('/pricing');
            return;
          }
        }
        const pendingInvite = getPendingInviteToken();
        if (pendingInvite) {
          navigate(`/join?token=${encodeURIComponent(pendingInvite)}`, { replace: true });
          return;
        }
        // Paid subscription required for app access (SubscriptionGate); pricing is the default entry.
        if (result.hasActiveSubscription) {
          navigate(
            homeFor(result.user?.tenantId, {   // where you left off in this workspace (§8a), else its home
              canWorkbench: canAccessNetworkWorkbench(result.user?.stepflowRole),
              fallback: canAccessNetworkWorkbench(result.user?.stepflowRole) ? workbenchLanding() : '/agentic' }),
          );
        } else {
          navigate('/pricing');
        }
      } else {
        // Recovery path: account exists but is still UNCONFIRMED. The user
        // either lost the original code (24h TTL) or never opened the email.
        // Route them straight to the verify view with a fresh code already
        // dispatched so they don't have to click an extra "Resend" button.
        //
        // The verify form lives inside the signup-view container (it's
        // rendered when ``activeView === 'signup' && awaitingConfirmation``),
        // so we have to flip both pieces of state. We set ``activeView``
        // directly rather than calling ``switchView`` because that helper
        // ALSO resets ``awaitingConfirmation`` to false after a 300ms
        // animation — which would immediately undo what we're trying to do.
        if (result.errorCode === 'UserNotConfirmedException') {
          const email = loginData.email.trim();
          setSignupData((prev) => ({ ...prev, email, confirmEmail: email }));
          setActiveView('signup');
          setDirection(1);
          setAwaitingConfirmation(true);
          setError('');
          setSuccess(
            `Your account isn't verified yet. We're sending a fresh code to ${email}.`,
          );
          setLoading(false);
          // Fire-and-forget resend; failure surfaces via setError but does
          // not block the user — the page already has its own "Resend code"
          // button if this background call gets rate-limited.
          resendSignUp(email)
            .then((r) => {
              if (r?.success) {
                setSuccess(
                  `A fresh verification code has been sent to ${email}. Check your inbox (and spam).`,
                );
              } else if (r?.error) {
                setError(r.error);
              }
            })
            .catch((resendErr) => {
              setError(resendErr?.message || 'Could not send a new verification code.');
            });
          return;
        }
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const email = signupData.email.trim();
    const confirm = signupData.confirmEmail.trim();
    if (email !== confirm) {
      setError('Email addresses do not match.');
      return;
    }
    setLoading(true);

    try {
      const result = await signUp(email, signupData.password, signupData.name.trim());
      
      if (result.success) {
        setAwaitingConfirmation(true);
        setSuccess('Account created! Please check your email for the verification code.');
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle Confirmation
  const handleConfirmation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await confirmSignUp(signupData.email.trim(), confirmationCode);
      
      if (result.success) {
        if (isStripeCheckoutEnabled()) {
          setSuccess('Account verified!');
          setShowPostVerifyBilling(true);
        } else {
          setSuccess('Account verified! You can now log in.');
          setTimeout(() => {
            switchView('login');
          }, 2000);
        }
      } else {
        setError(result.error || 'Verification failed');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  // Resend the verification code (e.g. user lost the email or it landed in spam).
  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const email = signupData.email.trim();
      if (!email) {
        setError('Email address is missing — please go back to sign up again.');
        return;
      }
      const result = await resendSignUp(email);
      if (result.success) {
        setSuccess('A new verification code has been sent. Check your inbox (and spam).');
      } else {
        setError(result.error || 'Failed to resend verification code');
      }
    } catch (err) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  // Handle Forgot Password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await forgotPassword(forgotUsername);
      
      if (result.success) {
        setAwaitingReset(true);
        setSuccess('Verification code sent to your email!');
      } else {
        setError(result.error || 'Failed to send verification code');
      }
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // Handle Password Reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await confirmPassword(
        forgotUsername,
        resetData.code,
        resetData.newPassword
      );
      
      if (result.success) {
        setSuccess('Password reset successful! You can now log in.');
        setTimeout(() => {
          switchView('login');
        }, 2000);
      } else {
        setError(result.error || 'Password reset failed');
      }
    } catch (err) {
      setError(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              StepFlow AI
            </span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Home
          </button>
        </div>

        {/* Auth Forms Container - Fixed Height with Smooth Transitions */}
        <div className="relative min-h-[640px]">
          {/* Error/Success Messages - Outside animation container */}
          {(error || success) && (
            <div className="mb-6 absolute -top-20 left-0 right-0">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  {success}
                </div>
              )}
            </div>
          )}

          {/* Login View */}
          <div
            className={`absolute inset-0 transition-all duration-300 ease-in-out ${
              activeView === 'login'
                ? 'opacity-100 translate-x-0'
                : direction === 1
                ? 'opacity-0 -translate-x-full pointer-events-none'
                : 'opacity-0 translate-x-full pointer-events-none'
            }`}
          >
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Welcome Back</h2>
                <p className="text-gray-600">Login to continue building workflows</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <label htmlFor="login-email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="you@company.com"
                    className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="login-password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              {isGoogleOAuthConfigured() && (
                <div className="space-y-3 pt-2">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-500">
                        or
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleGoogleSignIn}
                    className="w-full h-12 flex items-center justify-center gap-2 border-2 border-gray-200 bg-white hover:bg-gray-50 text-gray-800 text-base font-medium rounded-lg transition-all disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </button>
                </div>
              )}
              {isGoogleOAuthConfigured() && (
                /* any company identity provider — email first (LOGIN-TO-TENANT.md §5) */
                <div className="mt-3">
                  <CompanySsoSignIn onBeforeRedirect={() => initializeCognitoConfig()} />
                </div>
              )}

              <div className="space-y-4 pt-6">
                <div className="text-center text-sm text-gray-600">
                  Forgot your password?{' '}
                  <button
                    onClick={() => switchView('reset')}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Reset it here
                  </button>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-500">
                      Don't have an account?
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => switchView('signup')}
                  className="w-full h-12 border-2 border-gray-300 hover:border-gray-400 text-gray-700 text-base font-medium rounded-lg transition-all"
                >
                  Create Account
                </button>
              </div>
            </div>
          </div>

          {/* Signup View */}
          <div
            className={`absolute inset-0 transition-all duration-300 ease-in-out ${
              activeView === 'signup'
                ? 'opacity-100 translate-x-0'
                : direction === 1
                ? 'opacity-0 translate-x-full pointer-events-none'
                : 'opacity-0 -translate-x-full pointer-events-none'
            }`}
          >
            {!awaitingConfirmation ? (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-2">Create Account</h2>
                  <p className="text-gray-600">Start building intelligent workflows</p>
                </div>

                {isStripeCheckoutEnabled() && (
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-4 py-3 text-sm text-emerald-900">
                    <div className="flex items-start gap-2">
                      <Shield className="h-5 w-5 shrink-0 text-emerald-700 mt-0.5" aria-hidden />
                      <p>
                        After email verification, you can add a default payment method on{' '}
                        <strong className="font-semibold">Stripe’s secure page</strong>. StepFlow never stores your card
                        number — billing is PCI-compliant by design.
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSignup} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      placeholder="you@company.com"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="signup-confirm-email" className="block text-sm font-medium text-gray-700">
                      Confirm email
                    </label>
                    <input
                      id="signup-confirm-email"
                      type="email"
                      autoComplete="email"
                      value={signupData.confirmEmail}
                      onChange={(e) => setSignupData({ ...signupData, confirmEmail: e.target.value })}
                      placeholder="Re-enter your email"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="signup-fullname" className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      id="signup-fullname"
                      type="text"
                      value={signupData.name}
                      onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                      placeholder="Enter your full name"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <input
                      id="signup-password"
                      type="password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      placeholder="Choose a strong password"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500">
                      At least 10 characters, with uppercase, lowercase, and a number.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating Account...' : 'Sign Up'}
                  </button>
                </form>

                {isGoogleOAuthConfigured() && (
                  <div className="space-y-3 pt-2">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-500">
                          or
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleGoogleSignIn}
                      className="w-full h-12 flex items-center justify-center gap-2 border-2 border-gray-200 bg-white hover:bg-gray-50 text-gray-800 text-base font-medium rounded-lg transition-all disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Continue with Google
                    </button>
                  </div>
                )}

                <div className="space-y-4 pt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-500">
                        Already have an account?
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => switchView('login')}
                    className="w-full h-12 border-2 border-gray-300 hover:border-gray-400 text-gray-700 text-base font-medium rounded-lg transition-all"
                  >
                    Login
                  </button>

                  <div className="text-center text-sm text-gray-600">
                    Forgot your password?{' '}
                    <button
                      onClick={() => switchView('reset')}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Reset it here
                    </button>
                  </div>
                </div>
              </div>
            ) : !showPostVerifyBilling ? (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-2">Verify Your Account</h2>
                  <p className="text-gray-600">
                    Enter the code sent to{' '}
                    <span className="font-medium text-gray-800">{signupData.email.trim()}</span>
                  </p>
                </div>

                <form onSubmit={handleConfirmation} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="confirmation-code" className="block text-sm font-medium text-gray-700">
                      Verification Code
                    </label>
                    <input
                      id="confirmation-code"
                      type="text"
                      value={confirmationCode}
                      onChange={(e) => setConfirmationCode(e.target.value)}
                      placeholder="Enter verification code"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Verify Account'}
                  </button>

                  <div className="flex items-center justify-between text-sm pt-1">
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={loading}
                      className="font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Resend code
                    </button>
                    <button
                      type="button"
                      onClick={() => switchView('login')}
                      disabled={loading}
                      className="font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Back to sign in
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-md">
                    <CreditCard className="h-7 w-7" aria-hidden />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Billing (optional next step)</h2>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Log in once, then we&apos;ll send you to <strong>Stripe Checkout</strong> to save a payment method.
                    Your card details stay with Stripe — we only receive a secure reference for future charges.
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 space-y-2">
                  <div className="flex items-center gap-2 text-gray-900 font-medium">
                    <Shield className="h-4 w-4 text-emerald-600 shrink-0" aria-hidden />
                    No card data on StepFlow servers
                  </div>
                  <p className="text-xs text-gray-500 pl-6">
                    Uses Stripe-hosted fields and vaulting. Enable test mode in Stripe Dashboard while developing.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem(STRIPE_SETUP_PENDING_KEY, '1');
                    setShowPostVerifyBilling(false);
                    setAwaitingConfirmation(false);
                    switchView('login');
                  }}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all"
                >
                  Log in &amp; open Stripe Checkout
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowPostVerifyBilling(false);
                    setAwaitingConfirmation(false);
                    switchView('login');
                  }}
                  className="w-full h-11 text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Skip for now — I&apos;ll add this later
                </button>
              </div>
            )}
          </div>

          {/* Reset View */}
          <div
            className={`absolute inset-0 transition-all duration-300 ease-in-out ${
              activeView === 'reset'
                ? 'opacity-100 translate-x-0'
                : direction === 1
                ? 'opacity-0 translate-x-full pointer-events-none'
                : 'opacity-0 -translate-x-full pointer-events-none'
            }`}
          >
            {!awaitingReset ? (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
                  <p className="text-gray-600">We'll send you a verification code</p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="reset-identifier" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="reset-identifier"
                      type="email"
                      autoComplete="email"
                      value={forgotUsername}
                      onChange={(e) => setForgotUsername(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending Code...' : 'Send Verification Code'}
                  </button>
                </form>

                <div className="space-y-4 pt-6">
                  <div className="text-center text-sm text-gray-600">
                    Remember your password?{' '}
                    <button
                      onClick={() => switchView('login')}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      Login here
                    </button>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-500">
                        Don't have an account?
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => switchView('signup')}
                    className="w-full h-12 border-2 border-gray-300 hover:border-gray-400 text-gray-700 text-base font-medium rounded-lg transition-all"
                  >
                    Create Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-2">Set New Password</h2>
                  <p className="text-gray-600">Enter the code and your new password</p>
                </div>

                <form onSubmit={handlePasswordReset} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700">
                      Verification Code
                    </label>
                    <input
                      id="reset-code"
                      type="text"
                      value={resetData.code}
                      onChange={(e) => setResetData({ ...resetData, code: e.target.value })}
                      placeholder="Enter verification code"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="reset-newpassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      id="reset-newpassword"
                      type="password"
                      value={resetData.newPassword}
                      onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                      placeholder="Enter new password"
                      className="w-full h-12 px-4 text-base border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-sm text-gray-500 mt-8">
          By continuing, you agree to our Terms of Service and Privacy Policy.
          {isStripeCheckoutEnabled() ? (
            <span className="block mt-1">
              Payment details are collected and stored by Stripe, Inc.
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
